import React, { useState } from 'react';
import './register.scss';
import DriveFolderUploadIcon from '@mui/icons-material/DriveFolderUpload';
import { Link, useNavigate } from 'react-router-dom';
import { Upload } from '@aws-sdk/lib-storage';
import { S3Client } from '@aws-sdk/client-s3';

const Register = () => {
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;

  const [uploadStatus, setUploadStatus] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const [formData, setFormData] = useState({
    username: '',
    name: '',
    email: '',
    address: '',
    phone: '',
    password: '',
    ssmno: '',
    sstid: '',
    sstPercentage: 8,
    serviceChargePercentage: 10,
  });

  const [icFile, setIcFile] = useState(null);
  const [ssmFile, setSsmFile] = useState(null);
  const [bankStatementFile, setBankStatementFile] = useState(null);

  const [icPreviewUrl, setIcPreviewUrl] = useState('');
  const [ssmPreviewUrl, setSsmPreviewUrl] = useState('');
  const [bankStatementPreviewUrl, setBankStatementPreviewUrl] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        name === 'sstPercentage' || name === 'serviceChargePercentage'
          ? value === ''
            ? ''
            : Number(value)
          : value,
    }));
  };

  const handleFileChange = (e, type) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    const preview = selectedFile.type.startsWith('image/')
      ? URL.createObjectURL(selectedFile)
      : '';

    if (type === 'ic') {
      setIcFile(selectedFile);
      setIcPreviewUrl(preview);
    } else if (type === 'ssm') {
      setSsmFile(selectedFile);
      setSsmPreviewUrl(preview);
    } else if (type === 'bankStatement') {
      setBankStatementFile(selectedFile);
      setBankStatementPreviewUrl(preview);
    }
  };

  const uploadToS3 = async (file, folder = 'merchant') => {
    try {
      const safeName = file.name.replace(/\s+/g, '-');
      const fileName = `${Date.now()}-${safeName}`;
      const key = `${folder}/${fileName}`;

      if (
        !process.env.REACT_APP_AWS_ACCESS_KEY_ID ||
        !process.env.REACT_APP_AWS_SECRET_ACCESS_KEY
      ) {
        throw new Error('AWS credentials are missing. Check your .env file.');
      }

      const credentials = {
        accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
        secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
      };

      const s3Client = new S3Client({
        region: process.env.REACT_APP_AWS_REGION,
        credentials,
      });

      const parallelUploadS3 = new Upload({
        client: s3Client,
        params: {
          Bucket: process.env.REACT_APP_S3_BUCKET,
          Key: key,
          Body: file,
          ContentType: file.type,
        },
        tags: [],
        queueSize: 4,
        partSize: 5 * 1024 * 1024,
      });

      parallelUploadS3.on('httpUploadProgress', (progress) => {
        if (progress.total) {
          const percentage = Math.round((progress.loaded / progress.total) * 100);
          setUploadStatus(`Uploading ${file.name}... ${percentage}%`);
        }
      });

      await parallelUploadS3.done();

      const s3Url = `https://${process.env.REACT_APP_S3_BUCKET}.s3.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/${key}`;
      return s3Url;
    } catch (error) {
      console.error('S3 Upload Error:', error);

      if (error.name === 'InvalidAccessKeyId') {
        throw new Error('Invalid AWS Access Key. Check your .env file.');
      } else if (error.name === 'SignatureDoesNotMatch') {
        throw new Error('AWS Secret Key is incorrect. Check your .env file.');
      } else if (error.name === 'NoSuchBucket') {
        throw new Error('S3 bucket does not exist or you do not have access.');
      } else if (error.code === 'AccessDenied') {
        throw new Error('Access denied. Check IAM permissions and CORS settings.');
      } else {
        throw new Error(error.message || 'Upload failed');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      username: '',
      name: '',
      email: '',
      address: '',
      phone: '',
      password: '',
      ssmno: '',
      sstid: '',
      sstPercentage: 8,
      serviceChargePercentage: 10,
    });

    setIcFile(null);
    setSsmFile(null);
    setBankStatementFile(null);

    setIcPreviewUrl('');
    setSsmPreviewUrl('');
    setBankStatementPreviewUrl('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMessage('');
    setErrorMessage('');
    setUploadStatus('');

    if (!formData.username || !formData.email || !formData.password) {
      setErrorMessage('Please fill username, email, and password');
      return;
    }

    try {
      setIsUploading(true);

      let icImageUrl = '';
      let ssmImageUrl = '';
      let bankStatementUrl = '';

      if (icFile) {
        icImageUrl = await uploadToS3(icFile, 'merchant/ic');
      }

      if (ssmFile) {
        ssmImageUrl = await uploadToS3(ssmFile, 'merchant/ssm');
      }

      if (bankStatementFile) {
        bankStatementUrl = await uploadToS3(bankStatementFile, 'merchant/bankstatement');
      }

      const apiData = {
        username: formData.username,
        name: formData.name,
        email: formData.email,
        address: formData.address,
        phone: formData.phone,
        password: formData.password,
        ssmno: formData.ssmno,
        sstid: formData.sstid,
        icImage: icImageUrl,
        ssmImage: ssmImageUrl,
        bankstatement: bankStatementUrl,
        sstPercentage: Number(formData.sstPercentage) || 0,
        serviceChargePercentage: Number(formData.serviceChargePercentage) || 0,
      };

      const response = await fetch(`${apiUrl}/merchants`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiData),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccessMessage('Merchant created successfully');
        setUploadStatus('All files uploaded successfully');
        setErrorMessage('');
        resetForm();

        setTimeout(() => {
          navigate('/login');
        }, 1000);
      } else {
        setSuccessMessage('');
        setErrorMessage(data.message || 'Error creating merchant');
      }
    } catch (error) {
      console.error(error);
      setSuccessMessage('');
      setErrorMessage(error.message || 'Something went wrong');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="register">
      <div className="newContainer">
        <div className="top">
          <h1>Register Merchant</h1>
        </div>

        <div className="bottom">
          <div className="right">
            {uploadStatus && (
              <div className="uploadStatus">
                <span>{uploadStatus}</span>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="formInput">
                <label>Merchant Username</label>
                <input
                  type="text"
                  name="username"
                  placeholder="Enter username"
                  value={formData.username}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="formInput">
                <label>Merchant Full Name</label>
                <input
                  type="text"
                  name="name"
                  placeholder="Enter merchant full name"
                  value={formData.name}
                  onChange={handleChange}
                />
              </div>

              <div className="formInput">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  placeholder="Enter email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="formInput">
                <label>Password</label>
                <input
                  type="password"
                  name="password"
                  placeholder="Enter password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="formInput">
                <label>Address</label>
                <input
                  type="text"
                  name="address"
                  placeholder="Enter address"
                  value={formData.address}
                  onChange={handleChange}
                />
              </div>

              <div className="formInput">
                <label>Phone</label>
                <input
                  type="text"
                  name="phone"
                  placeholder="Enter phone number"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>

              <div className="formInput">
                <label>SSM Number</label>
                <input
                  type="text"
                  name="ssmno"
                  placeholder="Enter SSM number"
                  value={formData.ssmno}
                  onChange={handleChange}
                />
              </div>

              <div className="formInput">
                <label>SST ID</label>
                <input
                  type="text"
                  name="sstid"
                  placeholder="Enter SST ID"
                  value={formData.sstid}
                  onChange={handleChange}
                />
              </div>

              <div className="formInput">
                <label>SST Percentage (%)</label>
                <input
                  type="number"
                  name="sstPercentage"
                  placeholder="8"
                  value={formData.sstPercentage}
                  onChange={handleChange}
                  min="0"
                  max="100"
                />
              </div>

              <div className="formInput">
                <label>Service Charge Percentage (%)</label>
                <input
                  type="number"
                  name="serviceChargePercentage"
                  placeholder="10"
                  value={formData.serviceChargePercentage}
                  onChange={handleChange}
                  min="0"
                  max="100"
                />
              </div>

              <div className="formInput">
                <label htmlFor="icFile">
                  IC Upload <DriveFolderUploadIcon className="icon" />
                </label>
                <input
                  type="file"
                  id="icFile"
                  accept="image/*,.pdf"
                  style={{ display: 'none' }}
                  onChange={(e) => handleFileChange(e, 'ic')}
                />
                {icPreviewUrl && <img src={icPreviewUrl} alt="IC Preview" />}
                {icFile && !icPreviewUrl && <span>{icFile.name}</span>}
              </div>

              <div className="formInput">
                <label htmlFor="ssmFile">
                  SSM Upload <DriveFolderUploadIcon className="icon" />
                </label>
                <input
                  type="file"
                  id="ssmFile"
                  accept="image/*,.pdf"
                  style={{ display: 'none' }}
                  onChange={(e) => handleFileChange(e, 'ssm')}
                />
                {ssmPreviewUrl && <img src={ssmPreviewUrl} alt="SSM Preview" />}
                {ssmFile && !ssmPreviewUrl && <span>{ssmFile.name}</span>}
              </div>

              <div className="formInput">
                <label htmlFor="bankStatementFile">
                  Bank Statement <DriveFolderUploadIcon className="icon" />
                </label>
                <input
                  type="file"
                  id="bankStatementFile"
                  accept="image/*,.pdf"
                  style={{ display: 'none' }}
                  onChange={(e) => handleFileChange(e, 'bankStatement')}
                />
                {bankStatementPreviewUrl && (
                  <img src={bankStatementPreviewUrl} alt="Bank Statement Preview" />
                )}
                {bankStatementFile && !bankStatementPreviewUrl && (
                  <span>{bankStatementFile.name}</span>
                )}
              </div>

              <div className="formInput">
                <button type="submit" disabled={isUploading}>
                  {isUploading ? 'Submitting...' : 'Submit'}
                </button>
              </div>

              <div className="formInput">
                {successMessage && <span style={{ color: 'green' }}>{successMessage}</span>}
                {errorMessage && <span style={{ color: 'red' }}>{errorMessage}</span>}
                <span>
                  You already have an account.{' '}
                  <Link to="/login" style={{ textDecoration: 'none' }}>
                    Login
                  </Link>
                </span>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;